import React, { useState } from "react";
import toast from "react-hot-toast";
import BOMAsyncSelect from "./BOMAsyncSelect";
import Button, { ButtonType } from "../../components/Assets/Button";
import { RootDispatch } from "../../store";
import { useDispatch } from "react-redux";
import { getComponents } from "../../store/slices/Factory/copyBomSlice";

interface CopyBOMModalProps {
  isOpen: boolean;
  onClose: () => void;
  from: "product" | "material";
  onAppendComponents: (components: any[]) => void;
}

const CopyBOMModal: React.FC<CopyBOMModalProps> = ({
  isOpen,
  onClose,
  from,
  onAppendComponents,
}) => {
  const dispatch = useDispatch<RootDispatch>();
  const [selectedOption, setSelectedOption] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);

  if (!isOpen) return null;

  const handleCopy = async () => {
    if (!selectedOption || !selectedOption.bomId) {
      return toast.error("Please select an item with a valid BOM");
    }

    setLoading(true);
    try {
      const res = await dispatch(
        getComponents({
          params: {
            from,
            bomId: selectedOption.bomId,
          },
        }),
      ).unwrap();
      if (res.success && res.data) {
        onAppendComponents(res.data);
        toast.success("BOM components copied successfully");
        onClose();
      } else {
        toast.error("Failed to load components");
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to copy BOM");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
        <div className="flex justify-between items-center border-b pb-3">
          <h3 className="text-lg font-bold text-gray-800">
            Copy BOM Components
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <div className="space-y-2">
          <label className="block text-xs font-semibold text-gray-600 uppercase">
            Select {from === "product" ? "Product" : "Material"}
          </label>
          <BOMAsyncSelect
            from={from}
            value={selectedOption}
            onChange={(opt) => setSelectedOption(opt)}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Button type={ButtonType.Cancel} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type={ButtonType.Submit}
            onClick={handleCopy}
            disabled={loading || !selectedOption}
            className="bg-navyBlue-primary text-white px-4"
          >
            {loading ? "Copying..." : "Copy BOM"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CopyBOMModal;
